import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  BookmarkCheck, 
  MapPin, 
  Clock, 
  Route as RouteIcon,
  Mountain,
  CornerDownRight,
  Trash2,
  AlertTriangle,
  Loader2,
  FolderOpen,
  Share2,
  Globe,
  Lock,
  Heart,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED_TAGS = [
  'scenic', 'mountain', 'coastal', 'countryside', 'historic', 
  'easy-drive', 'challenging', 'caravan-friendly', 'oldtimer-tested'
];

const SavedRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [shareRoute, setShareRoute] = useState(null);
  const [shareData, setShareData] = useState({ description: '', tags: [] });

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(`${API}/routes`);
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
      toast.error('Failed to load saved routes');
    } finally {
      setLoading(false);
    }
  };

  const deleteRoute = async (id) => {
    try {
      await axios.delete(`${API}/routes/${id}`);
      setRoutes(routes.filter(r => r.id !== id));
      toast.success('Route deleted');
    } catch (error) {
      console.error('Failed to delete route:', error);
      toast.error('Failed to delete route');
    }
    setDeleteId(null);
  };

  const openShareDialog = (route) => {
    setShareRoute(route);
    setShareData({
      description: route.description || '',
      tags: route.tags || []
    });
  };

  const toggleShare = async () => {
    if (!shareRoute) return;

    try {
      // First update description and tags if sharing
      if (!shareRoute.is_public) {
        await axios.post(`${API}/routes`, {
          ...shareRoute,
          description: shareData.description,
          tags: shareData.tags,
          is_public: true
        });
      }

      const response = await axios.put(`${API}/routes/${shareRoute.id}/share`);
      setRoutes(routes.map(r => 
        r.id === shareRoute.id 
          ? { ...r, is_public: response.data.is_public, description: shareData.description, tags: shareData.tags }
          : r
      ));
      
      if (response.data.is_public) {
        toast.success('Route shared with community!');
      } else {
        toast.success('Route is now private');
      }
    } catch (error) {
      console.error('Failed to toggle sharing:', error);
      toast.error('Failed to update sharing status');
    }
    setShareRoute(null);
  };

  const toggleTag = (tag) => {
    setShareData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full bg-background">
      <div className="p-6 md:p-8 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
            <BookmarkCheck className="w-8 h-8 text-primary" />
            Saved Routes
          </h1>
          <p className="text-muted-foreground mt-2">
            Your previously planned routes. Share them with the community!
          </p>
        </div>

        {routes.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <FolderOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No saved routes yet</h3>
              <p className="text-muted-foreground">
                Plan a route in the Route Planner and save it to see it here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="h-[calc(100vh-200px)]">
            <div className="space-y-4 pr-4">
              {routes.map((route) => (
                <Card key={route.id} className="bg-card border-border hover:border-primary/30 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="font-display text-xl text-foreground">
                            {route.name}
                          </CardTitle>
                          {route.is_public ? (
                            <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
                              <Globe className="w-3 h-3 mr-1" />
                              Shared
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                              <Lock className="w-3 h-3 mr-1" />
                              Private
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <MapPin className="w-3 h-3" />
                          {route.start_location?.name?.split(',')[0]} → {route.end_location?.name?.split(',')[0]}
                        </CardDescription>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary"
                          onClick={() => openShareDialog(route)}
                          data-testid={`share-route-${route.id}`}
                        >
                          <Share2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteId(route.id)}
                          data-testid={`delete-route-${route.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {route.description && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {route.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <RouteIcon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-foreground font-medium">
                          {route.distance?.toFixed(1)} km
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-foreground font-medium">
                          {Math.round(route.duration || 0)} min
                        </span>
                      </div>
                      {route.is_public && route.likes > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <Heart className="w-4 h-4 text-red-500" />
                          <span className="text-foreground font-medium">
                            {route.likes} likes
                          </span>
                        </div>
                      )}
                    </div>

                    {route.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {route.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs bg-muted text-muted-foreground">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {route.warnings?.length > 0 && (
                      <div className="pt-3 border-t border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-foreground">
                            {route.warnings.length} Warning{route.warnings.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {route.warnings.filter(w => w.type === 'gradient').length > 0 && (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                              <Mountain className="w-3 h-3 mr-1" />
                              {route.warnings.filter(w => w.type === 'gradient').length} Gradient
                            </Badge>
                          )}
                          {route.warnings.filter(w => w.type === 'bend').length > 0 && (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                              <CornerDownRight className="w-3 h-3 mr-1" />
                              {route.warnings.filter(w => w.type === 'bend').length} Bend
                            </Badge>
                          )}
                          {route.warnings.filter(w => w.severity === 'high').length > 0 && (
                            <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">
                              {route.warnings.filter(w => w.severity === 'high').length} High Severity
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 text-xs text-muted-foreground">
                      Saved: {new Date(route.created_at).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Share Dialog */}
      <Dialog open={!!shareRoute} onOpenChange={() => setShareRoute(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-display flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              {shareRoute?.is_public ? 'Update Shared Route' : 'Share Route'}
            </DialogTitle>
            <DialogDescription>
              {shareRoute?.is_public 
                ? 'Update your shared route or make it private again.'
                : 'Share this route with the community so others can discover it.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">Description</Label>
              <Textarea
                id="description"
                value={shareData.description}
                onChange={(e) => setShareData({ ...shareData, description: e.target.value })}
                placeholder="Describe what makes this route special..."
                className="bg-muted/50 border-border min-h-[80px]"
                data-testid="share-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Tags</Label>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_TAGS.map((tag) => (
                  <Badge
                    key={tag}
                    variant={shareData.tags.includes(tag) ? "default" : "outline"}
                    className={`cursor-pointer transition-colors ${
                      shareData.tags.includes(tag) 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => toggleTag(tag)}
                    data-testid={`share-tag-${tag}`}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1 border-border"
                onClick={() => setShareRoute(null)}
              >
                Cancel
              </Button>
              <Button 
                onClick={toggleShare}
                className={`flex-1 ${shareRoute?.is_public ? 'bg-muted text-foreground hover:bg-destructive hover:text-destructive-foreground' : 'bg-primary hover:bg-primary/90 text-primary-foreground'}`}
                data-testid="confirm-share-btn"
              >
                {shareRoute?.is_public ? (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Make Private
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 mr-2" />
                    Share with Community
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete Route?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This route will be permanently deleted from your saved routes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-muted text-foreground border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteRoute(deleteId)}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SavedRoutes;
